"use client";

import { useCallback } from "react";

import { CACHE_SESSION_KEYS_KEY, WRITE_DEX_SESSION_KEYS } from "@/constants";
import { EMPTY_OBJECT } from "@/utils/empty";

import { useLocalStorage, useStorage } from "./storage";

const createSessionMapKey = (accountAddress: string, address: string): string => {
  return `${accountAddress}-${address}`;
};

export const useCacheSessionKeysStorage = () => {
  return useLocalStorage<boolean>(CACHE_SESSION_KEYS_KEY, true);
};

export const useSessionKeys = () => {
  const [cacheSessionKeys] = useCacheSessionKeysStorage();

  const [sessionKeys, setSessionKeys] = useStorage<Record<string, string>>(
    WRITE_DEX_SESSION_KEYS,
    EMPTY_OBJECT,
    cacheSessionKeys ? "local" : "session",
  );

  const getSessionKeyForAccount = useCallback(
    (accountAddress: string, address: string): string | undefined => {
      const key = createSessionMapKey(accountAddress, address);
      return sessionKeys[key];
    },
    [sessionKeys],
  );

  const setSessionKeyForAccount = (
    accountAddress: string,
    address: string,
    sessionKey: string,
  ): void => {
    const key = createSessionMapKey(accountAddress, address);
    setSessionKeys({ ...sessionKeys, [key]: sessionKey });
  };

  const clearSessionKeyForSubaccount = (accountAddress: string, subaccountAddr: string): void => {
    const key = createSessionMapKey(accountAddress, subaccountAddr);
    setSessionKeys((prev) => {
      const newPrev = { ...prev };
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
      delete newPrev[key];
      return newPrev;
    });
  };

  const clearAllSessionKeys = () => {
    setSessionKeys({});
  };

  return {
    getSessionKeyForAccount,
    setSessionKeyForAccount,
    clearSessionKeyForSubaccount,
    clearAllSessionKeys,
  };
};
